import { useState } from 'react';
import {
  confirmFileUpload,
  deleteExperimentFile,
  startFileUpload,
  uploadFileToGCS,
} from '../registryApi';

/**
 * Drives the signed-URL two-step upload flow (StartFileUpload -> PUT to GCS ->
 * ConfirmFileUpload) for one experiment's data file, documents, or a treatment's
 * creatives. Mirrors FileUploader's upload/confirm shape, adapted for this app's
 * direct-to-GCS flow instead of streaming through Django.
 */
export default function useExperimentFileUpload({ experimentId, kind, treatmentId }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const uploadFile = async (file, documentCategory = '') => {
    setUploading(true);
    setError('');
    try {
      const {
        file_id: fileId,
        uri,
        upload_url: uploadUrl,
        upload_headers: uploadHeaders,
      } = await startFileUpload({
        experiment_id: experimentId,
        kind,
        original_filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        ...(treatmentId ? { treatment_id: treatmentId } : {}),
        ...(documentCategory ? { document_category: documentCategory } : {}),
      });
      await uploadFileToGCS(uploadUrl, file, uploadHeaders);
      const confirmBody =
        kind === 'data'
          ? { kind, experiment_id: experimentId, uri, file_name: file.name }
          : {};
      return await confirmFileUpload(fileId, confirmBody);
    } catch (e) {
      setError(e.message || 'Upload failed');
      throw e;
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (fileId) => {
    setError('');
    try {
      await deleteExperimentFile(fileId);
    } catch (e) {
      setError(e.message || 'Failed to delete file');
      throw e;
    }
  };

  return { uploadFile, removeFile, uploading, error, setError };
}
